import "./TestReportTypeEditComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {
    Field,
    FieldArray,
    FieldArrayRenderProps,
    FieldProps,
    Form,
    Formik,
    FormikHelpers,
    FormikProps,
    FormikValues
} from "formik";
import {Grid} from "@material-ui/core";
import FormikSelectDropdownComponent
    from "../../../../../shared/components/core/formik-select-dropdown/FormikSelectDropdownComponent";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FormikInputComponent from "../../../../../shared/components/core/formik-input/FormikInputComponent";
import AddIcon from "@mui/icons-material/Add";
import DialogComponent from "../../../../../shared/components/dialog/dialogComponent";
import _ from "lodash";
import commonService from "../../../../../shared/services/common.service";
import {CommonService} from "../../../../../shared/services";
import DetailsDataKeyValueComponent
    from "../../../../../shared/components/details-data-key-value-component/detailsDataKeyValueComponent";
import ChipComponent from "../../../../../shared/components/chip/ChipComponent";
import {ImageConfig} from "../../../../../constants";
import {DeleteForever} from "@mui/icons-material";
import RichTextEditorComponent, {
    isEmptyHtml,
    isMaximumLineCountReached
} from "../../../../../shared/components/rich-text-editor/RichTextEditorComponent";
import PdfPreviewComponent from "../../../../../shared/components/pdf-preview/pdfPreviewComponent";
import FeedbackComponent from "../../../../../shared/components/feedback/feedbackComponent";

interface TestReportTypeEditComponentProps {
    testDetails: any;
    onUpdateComplete: Function;
    onEditCancel: Function;
}

const testReportAddValidationSchema = Yup.object().shape({
    layout: Yup.string().required("Layout is required"),
    currentOption: Yup.object(), // Assuming currentOption is an object
    disclaimer: Yup.string()
        .required("Disclaimer is required")
        .test('html-validation', 'Content exceeds maximum allowed lines', function (value) {
            if (value) {
                return isMaximumLineCountReached(value); // Assuming default maxLines and maxCharsPerLine
            }
            return true; // Return true if value is empty or undefined
        }).test('empty-html-validation', 'Content is empty', function (value) {
            if (value) {
                return !isEmptyHtml(value);
            }
            return true; // Return true if value is empty or undefined
        }),
    tableTitle: Yup.array().when('currentOption.isMultipleTable', {
        is: true,
        then: Yup.array().of(
            Yup.object().shape({
                title: Yup.string().required("Title is required"),
                biomarkerIds: Yup.array()
                    .of(Yup.number().required())
                    .min(1, "Select at least one biomarker")
                    .required("Select biomarkers"),
            })
        ).required("At least one table is required"),
        otherwise: Yup.array().of(
            Yup.object().shape({
                title: Yup.string(), // Not required when isMultipleTable is false
                biomarkerIds: Yup.array().of(Yup.number()), // Not required when isMultipleTable is false
            })
        )
    }),
});

interface TableTitle {
    title: string;
    biomarkerIds: number[];
}

interface testReportAddSchema {
    layout: string;
    currentOption: object;
    disclaimer: string;
    tableTitle: TableTitle[];
}

const FormInitialValues: testReportAddSchema = {
    layout: "",
    currentOption: {},
    disclaimer: "",
    tableTitle: [
        {
            title: "",
            biomarkerIds: []
        }
    ]
}

const getAvailableBiomarkers = (values: any, allBiomarkers: any, index: number | undefined) => {
    try {
        // Extract selected biomarker IDs from the values object
        const selectedBiomarkerIds = values?.reduce((acc: number[], item: any, currentIndex: number) => {
            if (index === undefined || currentIndex !== index) {
                return acc.concat(item.biomarkerIds);
            }
            return acc;
        }, []);

        // Filter out the available biomarkers that are not selected
        const availableBiomarkers = allBiomarkers.filter((biomarker: any) => {
            return !selectedBiomarkerIds?.includes(biomarker.id);
        });

        return availableBiomarkers;
    } catch (error: any) {
        console.error("Failed to get unique biomarkers", error);
        return [];
    }
}

const TestReportTypeEditComponent = (props: TestReportTypeEditComponentProps) => {
    const {testDetails, onUpdateComplete, onEditCancel} = props;
    const [showAttachmentPreviewDialog, setShowAttachmentPreviewDialog] = useState(false);
    const [formInitialValue, setFormInitialValue] = useState(FormInitialValues);
    const formikRef = useRef<FormikProps<FormikValues>>(null);
    const [reportTypeOptions, setReportTypeOptions] = useState([])
    const [isLoaded, isDataLoaded] = useState(false);


    const getTestReportConfig = useCallback(() => {
        commonService._test.getTestReportType().then((response) => {
            setReportTypeOptions(response?.data)
        })
    }, [])

    const onSubmit = useCallback((values: any, {setSubmitting, setErrors, validateForm}: FormikHelpers<any>) => {
        const payload = _.cloneDeep(values);
        if (!payload?.currentOption?.isMultipleTable) {
            payload.tableTitle = [
                {
                    title: "",
                    biomarkerIds: testDetails?.biomarkerIds,
                }
            ]
        }
        delete payload.currentOption;
        setSubmitting(true);
        CommonService._test.addReportLayout(testDetails?.id, payload)
            .then((response: any) => {
                setSubmitting(false);
                onUpdateComplete(response?.data)
            }).catch((error: any) => {
            setSubmitting(false);
            CommonService._alert.showToast(error?.response?.data?.message, 'error');
        })
    }, [onUpdateComplete, testDetails?.id, testDetails?.biomarkerIds]);
    useEffect(() => {
        getTestReportConfig();
    }, [getTestReportConfig]);
    const handleRemoveTable = useCallback((index: any, arrayHelper: FieldArrayRenderProps) => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: "Are you sure you want to remove this table?",
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.ConfirmationImage}`,
            direction: "up",
            yes: {
                text: "Remove",
                color: "primary"
            },
            no: {
                text: "Cancel",
                color: "primary"
            }
        })
            .then(() => {
                arrayHelper?.remove(index);
            })
            .catch(() => {
            })
    }, []);

    const [pdfPreviewResponse, setPdfPreviewResponse] = useState<ArrayBuffer | null>(null);
    const [isPdfPreviewLoading, setIsPdfPreviewLoading] = useState<boolean>(false);

    const handleTestLayoutPreview = (values: FormikValues) => {
        const payload = _.cloneDeep(values);
        if (!payload?.currentOption?.isMultipleTable) {
            payload.tableTitle = [
                {
                    title: "",
                    biomarkerIds: testDetails?.biomarkerIds,
                }
            ]
        }
        delete payload.currentOption;
        payload.testName = testDetails.name;
        const toastId = CommonService._alert.loadingToast("Fetching layout preview");
        setIsPdfPreviewLoading(true);
        CommonService._test.previewTestPanel(payload).then((response) => {
            CommonService._alert.updateToast(toastId, response?.message || "Fetched Successfully", "success");
            setPdfPreviewResponse(response.data);
            setShowAttachmentPreviewDialog(true)
        }).catch((error) => {
            CommonService._alert.updateToast(toastId, error?.error || "Failed to fetch test layout", "error");
        }).finally(() => setIsPdfPreviewLoading(false))
    }

    useEffect(() => {
        if (reportTypeOptions) {
            setFormInitialValue((prev) => {
                const testLayoutDetails = testDetails?.testLayoutDetails?.[0];
                const [selectedOption] = reportTypeOptions.filter((item: any) => item?.code === testLayoutDetails?.layout)
                if (testLayoutDetails) {
                    return {
                        ...prev,
                        layout: testLayoutDetails?.layout,
                        currentOption: selectedOption,
                        disclaimer: testLayoutDetails?.disclaimer,
                        tableTitle: testLayoutDetails?.biomarkerObj?.map((table: any) => {
                            return {
                                title: table?.title,
                                biomarkerIds: table?.biomarkerIds,
                            }
                        })

                    }
                } else {
                    return {...prev}
                }
            })
            setTimeout(() => isDataLoaded(true), 100)

        }
    }, [reportTypeOptions, testDetails?.testLayoutDetails])

    return (
        <div className={'test-report-type-edit-component'}>
            <div className="t-form attachments-with-title-wrapper">
                <Formik
                    validationSchema={testReportAddValidationSchema}
                    initialValues={formInitialValue}
                    validateOnChange={false}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    validateOnMount={true}
                    innerRef={formikRef}
                    onSubmit={onSubmit}
                >
                    {({isSubmitting, values, dirty, isValid, setFieldValue, touched, errors, validateForm}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);
                        return (
                            <Form className={"test-panel-configuration-add-holder"}>
                                {/*{JSON.stringify(values)}*/}
                                <div className="t-form-controls-wrapper">
                                    <Grid container spacing={2} xs={12}>
                                        <Grid item xs={12} sm={12} md={12} xl={12}>
                                            <div className="attachment-input-wrapper">
                                                <div className="attachment-input-section">
                                                    <Field name={"layout"}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <div className={"select-component-no-margin"}>
                                                                    <FormikSelectDropdownComponent
                                                                        label={"Select Report Type"}
                                                                        required={true}
                                                                        valueKey={"code"}
                                                                        options={reportTypeOptions}
                                                                        formikField={field}
                                                                        onUpdate={(value: any) => {
                                                                            const currentOption = reportTypeOptions.find((item: any) => item?.code === value)
                                                                            setFieldValue("currentOption", currentOption)
                                                                        }}
                                                                    />
                                                                </div>
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                                <ButtonComponent
                                                    className="attachment-upload-button mrg-top-6"
                                                    disabled={!values?.currentOption || isPdfPreviewLoading}
                                                    color={"secondary"}
                                                    startIcon={<VisibilityIcon/>}
                                                    variant={"contained"}
                                                    fullWidth={false}
                                                    size={'large'}
                                                    handleClick={() => {
                                                        // setShowAttachmentPreviewDialog(true)
                                                        handleTestLayoutPreview(values)
                                                    }}>
                                                    View
                                                </ButtonComponent>
                                            </div>
                                            <Grid item xs={12} sm={12}>
                                                <FeedbackComponent className={"mrg-top-10 mrg-bottom-10"}>
                                                    The report is dynamic based on selected biomarkers and layout type.
                                                    Actual data appears after result generation.
                                                </FeedbackComponent>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={12} xl={12}>
                                            {
                                                getAvailableBiomarkers(values?.tableTitle, testDetails?.biomarker, undefined)?.length > 0 &&
                                                <DetailsDataKeyValueComponent title="Biomarkers">
                                                    {getAvailableBiomarkers(values?.tableTitle, testDetails?.biomarker, undefined)?.map((item: any) =>
                                                        <ChipComponent key={item.id}
                                                                       label={item.name}/>) || '-'
                                                    }
                                                </DetailsDataKeyValueComponent>
                                            }
                                            <div className="horizontal-line"/>
                                        </Grid>
                                    </Grid>
                                    {
                                        values?.currentOption?.isMultipleTable && (
                                            <FieldArray name={"tableTitle"} render={(arrayHelpers) => (
                                                <>
                                                    <div>
                                                        {
                                                            values?.tableTitle?.map((tableTitle: TableTitle, index: number) => {
                                                                return (
                                                                    <div className="pdd-top-10" key={index}>
                                                                        <Grid container spacing={2} xs={12}>
                                                                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                                                                <Field name={`tableTitle[${index}].title`}>
                                                                                    {
                                                                                        (field: FieldProps) => {
                                                                                            return <FormikInputComponent
                                                                                                required={true}
                                                                                                label={"Title name"}
                                                                                                placeholder={"Enter title name"}
                                                                                                formikField={field}/>
                                                                                        }
                                                                                    }
                                                                                </Field>
                                                                            </Grid>
                                                                            <Grid item xs={11} sm={11} md={5} xl={5}>
                                                                                <Field
                                                                                    name={`tableTitle[${index}].biomarkerIds`}>
                                                                                    {
                                                                                        (field: FieldProps) => {
                                                                                            return <FormikSelectDropdownComponent
                                                                                                label={"Biomarker Ids"}
                                                                                                required={true}
                                                                                                options={getAvailableBiomarkers(values?.tableTitle, testDetails?.biomarker, index)}
                                                                                                formikField={field}
                                                                                                multiple={true}
                                                                                                valueKey={"id"}
                                                                                                getOptionLabel={(option) => option?.name}
                                                                                            />
                                                                                        }
                                                                                    }
                                                                                </Field>
                                                                            </Grid>
                                                                            <Grid item xs={1} sm={1} md={1} xl={1}>
                                                                                <ButtonComponent
                                                                                    variant={"icon"}
                                                                                    color={"error"}
                                                                                    size={"small"}
                                                                                    // position={"end"}
                                                                                    className={"mrg-top-10"}
                                                                                    disabled={values?.tableTitle.length <= 1}
                                                                                    handleClick={() => handleRemoveTable(index, arrayHelpers)}>
                                                                                    <DeleteForever/>
                                                                                </ButtonComponent>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </div>
                                                                )
                                                            })
                                                        }
                                                        <ButtonComponent
                                                            variant={"text"}
                                                            fullWidth={false}
                                                            type={'button'}
                                                            className={"mrg-top-10"}
                                                            color={"info"}
                                                            disabled={getAvailableBiomarkers(values?.tableTitle, testDetails?.biomarker, undefined).length === 0 || !!(errors?.tableTitle)}
                                                            startIcon={
                                                                <AddIcon/>}
                                                            handleClick={() => arrayHelpers.push({
                                                                title: "",
                                                                biomarkerIds: []
                                                            })}
                                                        >
                                                            Add More
                                                        </ButtonComponent>
                                                    </div>
                                                    <div className="horizontal-line"/>
                                                </>
                                            )}/>
                                        )
                                    }
                                    <div className="textarea-wrapper">
                                        {
                                            isLoaded && <Field name={'disclaimer'}>
                                                {
                                                    (field: FieldProps) => (
                                                        // <FormikInputComponent
                                                        //     label={'Disclaimer'}
                                                        //     placeholder={'Disclaimer'}
                                                        //     type={"text"}
                                                        //     required={false}
                                                        //     multiline={true}
                                                        //     rows={3}
                                                        //     formikField={field}
                                                        // />
                                                        <RichTextEditorComponent
                                                            label={'Disclaimer'}
                                                            required={true}
                                                            value={field?.field?.value}
                                                            errorMessage={_.get(errors, field?.field?.name)}
                                                            onChange={(value: string) => {
                                                                setFieldValue(field?.field?.name, value)
                                                            }}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        }
                                    </div>
                                </div>
                                <div className="form-action-section-edit">
                                    <ButtonComponent
                                        className={"width-25"}
                                        color={"secondary"}
                                        type={"button"}
                                        variant={"outlined"}
                                        disabled={isSubmitting}
                                        fullWidth={false}
                                        handleClick={() => {
                                            onEditCancel()
                                        }}
                                    >
                                        Cancel
                                    </ButtonComponent>
                                    <ButtonComponent
                                        className={"width-25"}
                                        type="submit"
                                        color={"primary"}
                                        variant={"contained"}
                                        fullWidth={false}
                                        // If multiple tables are used, all biomarkers need to be assigned to their respective tables. If a single table is used, all biomarkers will be assigned to the first table.
                                        disabled={!(isValid) || (values?.isMultipleTable && getAvailableBiomarkers(values?.tableTitle, testDetails?.biomarker, undefined).length > 0)}
                                        isLoading={isSubmitting}
                                    >
                                        Save Changes
                                    </ButtonComponent>
                                </div>
                            </Form>
                        )
                    }}
                </Formik>
            </div>
            <DialogComponent open={showAttachmentPreviewDialog}
                             maxWidth={'md'}
                             title={formikRef?.current?.values?.currentOption?.title}
                             isShowCancel={true}
                             onClose={() => {
                                 setShowAttachmentPreviewDialog(false);
                             }}
                             className={"attachment-preview-dialog"}>
                <div className="attachment-preview-dialog-body" id="file_preview_component">
                    {/*<PdfPreviewComponent*/}
                    {/*    attachmentUrl={formikRef?.current?.values?.currentOption?.url}*/}
                    {/*    width={"100%"}*/}
                    {/*/>*/}
                    {/*<TestLayoutPdfComponent/>*/}
                    {pdfPreviewResponse && <PdfPreviewComponent
                        arrayBuffer={pdfPreviewResponse as any}
                        width={"100%"}
                    />}
                </div>
                {formikRef?.current?.values?.currentOption?.isMultipleTable &&
                    <div className="uploaded-attachment-file-name">
                        <span className="font-weight-bold">Selected Report Layout:</span>
                        <span>{formikRef?.current?.values?.currentOption?.title}</span>
                    </div>
                }
                <div className="attachment-preview-dialog-footer">
                    {
                        <ButtonComponent variant={"outlined"}
                                         color={'secondary'}
                                         id="continue_btn"
                                         fullWidth={true}
                                         handleClick={() => {
                                             setShowAttachmentPreviewDialog(false)
                                         }}>
                            Close
                        </ButtonComponent>
                    }
                </div>
            </DialogComponent>
        </div>
    );

};

export default TestReportTypeEditComponent;
